import { faAdd, faArchive, faBoxOpen, faCheckCircle, faTrash, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, ListGroup, ListGroupItem, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import { deleteGoal, patchGoal, postTask } from "../../../../services/httpClient";
import { Goal } from "../../../../types/goal";
import Input from "../../../ui/Input";
import TaskItem from "./TaskItem";
import { Task } from "../../../../types/task";
import { Tooltip } from "react-tooltip";
import IconEmbeddedButton from "../../../ui/IconEmbeddedButton";
import { User } from "../../../../types/user";
import SelectFamily from "../../../ui/SelectFamily";
import IconButton from "../../../ui/IconButton";

interface GoalCardState {
  goal: Goal;
  family: User[];
  fetchGoals?: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  myCase?: boolean;
  setErrMsg: any;
}

const GoalCard = ({ goal, family, fetchGoals, setShowToast, setToastText, setToastBody, myCase, setErrMsg }: GoalCardState) => {
  const { id } = useParams();
  const [isDeleting, setIsDeleting] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<Goal>();
  const [newTask, setNewTask] = useState<string>("");
  const [showAddTask, setShowAddTask] = useState<boolean>(false);
  const [completed, setCompleted] = useState<boolean | number>();
  const [archived, setArchived] = useState<boolean | number>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [initialReadOnly, setInitialReadOnly] = useState<boolean>(true);

  useEffect(() => {
    setCompleted(goal.is_completed);
    setArchived(goal.is_archived);
    const isDeleted = family.find((f) => f.id == goal?.subject?.id)?.isDeleted;
    if (isDeleted === undefined) {
      setInitialReadOnly(!myCase);
    } else {
      setInitialReadOnly(!myCase || isDeleted);
    }
  }, []);

  const onClickAddTask = () => {
    setErrMsg();
    setShowAddTask(true);
  };

  const onClickSaveTask = () => {
    if (!newTask) {
      setShowAddTask(false);
      setErrMsg("Cannot set task name to null");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }
    const task = {
      name: newTask,
      is_next: false,
      is_completed: false,
      is_cm_reviewed: false,
      is_archived: false,
    };

    setErrMsg("");

    postTask(task, id, goal.id)
      .then((res: any) => {
        setShowAddTask(false);
        setNewTask("");
        fetchGoals();
        setShowToast(true);
        setToastText("Task Created");
        setToastBody(newTask);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelTask = () => {
    setErrMsg("");
    setShowAddTask(false);
  };

  const handleCompletedClick = () => {
    setErrMsg("");
    if (goal.is_archived !== 1 && goal.is_completed !== 1) {
      const goalChanges = {
        is_completed: !completed,
      };

      patchGoal(goalChanges, id, goal.id)
        .then((res: any) => {
          setCompleted(!completed);
          fetchGoals();
          setShowToast(true);
          setToastText("Goal Completed");
          setToastBody(goal.name);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          window.scrollTo({
            top: 0,
            behavior: "auto",
          });
        });
    }
  };

  const handleArchivedClick = () => {
    const goalChanges = {
      is_archived: !archived,
    };

    setErrMsg("");

    patchGoal(goalChanges, id, goal.id)
      .then((res: any) => {
        fetchGoals();
        setShowToast(true);
        setToastText(archived ? "Goal Published" : "Goal Archived");
        setToastBody(goal.name);
        setArchived(!archived);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickDelete = () => {
    setErrMsg("");
    setIsDeleting(true);
    setGoalToDelete(goal);
  };

  const onClickDeleteFinal = () => {
    setErrMsg("");
    deleteGoal(id, goalToDelete?.id)
      .then((res: any) => {
        setIsDeleting(false);
        setGoalToDelete(undefined);
        fetchGoals();
        setShowToast(true);
        setToastText("Goal Deleted");
        setToastBody(goalToDelete?.name);
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setErrMsg("");
    setIsDeleting(false);
    setGoalToDelete(undefined);
  };

  const onClickSubmit = (e: any) => {
    e?.preventDefault();
    setErrMsg("");
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light mb-3">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} className="col-6">
              <Card.Subtitle>Are you sure you want to delete - {goalToDelete?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const onClickEditFamily = () => {
    setIsEditing(true);
  };

  const onClickCancelEditFamily = () => {
    setIsEditing(false);
  };

  const onClickUpdateFamily = (e: any) => {
    let subject: any;
    const familyMember = (e?.target?.value).toString();

    if (familyMember.toLowerCase().includes("unassigned")) {
      subject = { subject: null };
    } else {
      const [subjectId, subjectType] = familyMember.split("|");
      subject = { subject: { id: subjectId, type: subjectType } };
    }

    patchGoal(subject, id, goal.id)
      .then((res: any) => {
        setToastText("Goal Updated");
        setToastBody("Member Updated");
        setShowToast(true);
        setIsEditing(false);
        fetchGoals();
      })
      .catch((err: any) => {
        console.error(err);
        setErrMsg("Failed to update household member");
      });
  };

  return (
    <>
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <Card key={goal.id} className="bg-light mb-3">
          <Card.Body>
            <Row>
              <Col className="col-9">
                <h5 className={goal.is_archived ? "text-muted" : "app-orange-text"}>{goal.name}</h5>
              </Col>
              <Col className="text-end">
                {/* {n.fields.date && <label className="note-type text-muted me-3">{n.fields.date}</label>} */}

                <Badge bg="secondary" className={goal.is_completed === 1 ? "ms-auto me-0" : "ms-auto me-3"}>
                  {goal.category_name}
                </Badge>
                {myCase && (
                  <>
                    {goal.is_archived !== 1 && (
                      <>
                        <FontAwesomeIcon
                          data-tooltip-id="tooltip-goal-complete"
                          data-tooltip-content="Complete Goal"
                          className={completed ? "app-green-text me-3 cursor-pointer" : "text-muted me-3 cursor-pointer"}
                          icon={faCheckCircle}
                          onClick={() => handleCompletedClick()}
                        />
                        <Tooltip id="tooltip-goal-complete" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                      </>
                    )}
                    {goal.is_completed !== 1 && (
                      <>
                        <FontAwesomeIcon
                          data-tooltip-id="tooltip-goal-archive"
                          data-tooltip-content={goal.is_archived === 1 ? "Unarchive Goal" : "Archive Goal"}
                          className={"text-muted me-3 cursor-pointer"}
                          icon={goal.is_archived === 1 ? faBoxOpen : faArchive}
                          onClick={() => handleArchivedClick()}
                        />
                        <Tooltip id="tooltip-goal-archive" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                      </>
                    )}
                    {goal.is_archived !== 1 && goal.is_completed !== 1 && (
                      <>
                        <FontAwesomeIcon
                          data-tooltip-id="tooltip-goal-delete"
                          data-tooltip-content="Delete Goal"
                          className="text-muted me-1 cursor-pointer"
                          icon={faTrash}
                          onClick={(e) => onClickDelete()}
                        />
                        <Tooltip id="tooltip-goal-delete" place="top" style={{ padding: "5px 10px", zIndex: "1100" }} />
                      </>
                    )}
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <h6>Tasks</h6>
            </Row>
            <Row className="mb-3">
              <ListGroup className="pe-0">
                {goal.tasks.map((t: Task) => (
                  <ListGroupItem key={t.id}>
                    <TaskItem
                      task={t}
                      goalArchived={goal.is_archived}
                      goalCompleted={goal.is_completed}
                      fetchGoals={fetchGoals}
                      goalId={goal.id}
                      setShowToast={setShowToast}
                      setToastText={setToastText}
                      setToastBody={setToastBody}
                      myCase={myCase}
                    />
                  </ListGroupItem>
                ))}
              </ListGroup>
            </Row>
            {myCase && goal.is_archived !== 1 && goal.is_completed !== 1 && !showAddTask && !goal?.goal_template_id && (
              <Row className="mb-3">
                <Col xs={12} className="d-flex justify-content-end px-0">
                  <IconEmbeddedButton onClickButton={() => onClickAddTask()} tooltipText="Add Task" icon={faAdd} />
                </Col>
              </Row>
            )}
            {showAddTask && (
              <>
                <Form onSubmit={(e: any) => onClickSubmit(e)}>
                  <Row>
                    <Col xs={12}>
                      <Stack direction="horizontal" gap={3}>
                        <Stack style={{ justifyContent: "center" }} className="col-6">
                          <Input
                            label="Enter New Task"
                            type="text"
                            value={newTask}
                            hasFocus={true}
                            onUpdate={(e: any) => setNewTask(e.target.value)}
                          />
                        </Stack>
                        <Button type="submit" className="app-secondary-color button" onClick={() => onClickSaveTask()}>
                          Save
                        </Button>
                        <div className="vr" />
                        <Button className="app-primary-color button" onClick={() => onClickCancelTask()}>
                          Cancel
                        </Button>
                      </Stack>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
            <Row>
              <Col className="col-fill">
                {isEditing ? (
                  <Stack direction="horizontal" gap={3}>
                    <SelectFamily
                      family={family}
                      familyUser={goal?.subject ? `${goal.subject.id}|${goal.subject.type}` : "unassigned"}
                      updateFamilyUser={onClickUpdateFamily}
                      readOnly={initialReadOnly}
                      includeLabel={false}
                      skipDefaultSelection={true}
                    />
                    <IconButton onClickButton={() => onClickCancelEditFamily()} tooltipText={"Close"} icon={faCheck} />
                  </Stack>
                ) : (
                  <>
                    <Badge
                      data-tooltip-id="tooltip"
                      data-tooltip-content={goal.is_archived === 1 || goal.is_completed === 1 ? "" : "Edit Member"}
                      bg="secondary"
                      className={goal.is_archived === 1 || goal.is_completed === 1 ? "cursor-not-allowed" : "cursor-pointer"}
                      onClick={() => {
                        if (!(goal.is_archived === 1 || goal.is_completed === 1)) {
                          onClickEditFamily();
                        }
                      }}
                    >
                      {goal.subject?.name || "Unassigned"}
                    </Badge>
                  </>
                )}
              </Col>
              <Col className="text-end">
                {goal.goal_template_id ? (
                  <Badge className="app-primary-color-inverse" bg="none">
                    {`Created ${goal.created_at} From Template`}
                  </Badge>
                ) : (
                  <Badge className="app-primary-color-inverse" bg="none">
                    {`Created ${goal.created_at}`}
                  </Badge>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default GoalCard;
